import {
  Box,
  Checkbox,
  Chip,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
  Skeleton,
  Button,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import CreateOrJoinOrg from './CreateOrJoinOrg';
import { FC, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getOrganization,
  removeUserApi,
  updateMemberSectionsApi,
} from '../../utils/apis';
import { IGetOrganization } from '../../schemas/apis/interface';
import theme from '../../theme';
import useCopyToClipboard from '../../hooks/useCopyToClipboard';
import {
  Copy,
  Check,
  Trash,
  EnvelopeSimple,
  CaretDown,
  Dot,
} from '@phosphor-icons/react';
import AddMemberToOrganisationModal from '../modals/AddMemberToOrganisationMOdal';
import DeleteModal from '../modals/DeleteModal';
import { useUser } from '../../hooks/useUser';
import { useAlert } from '../../hooks/useAlert';
import MessagePopup from '../common/MessagePopup';

const SECTION_LABEL_MAP: Record<string, string> = {
  agents: 'Agents',
  call_logs: 'Call Logs',
  knowledge_base: 'Knowledge Base',
  voice_library: 'Voice Library',
  phone_numbers: 'Phone Numbers',
  whatsapp_chats: 'WhatsApp Chats',
  agent_analytics: 'Analytics',
  order_management: 'Order Management',
  reservation_management: 'Reservation Management',
  store_management: 'Outlet Management',
  brand_management: 'Brand Management',
  billing: 'Billing',
  campaign: 'Campaign',
};

const SECTION_OPTIONS = Object.entries(SECTION_LABEL_MAP).map(
  ([key, label]) => ({ key, label })
);

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    borderRadius: '12px',
    border: '1px solid #DCE0E7',
    backgroundColor: '#FFFFFF',
    height: '100%',
    width: '100%',
    p: '24px',
    cursor: 'context-menu',
    overflowY: 'scroll',
    scrollbarWidth: 'none',
  },
  header: {
    mb: 0.5,
    display: 'flex',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: '20px',
    fontWeight: 600,
    color: theme.palette.custom.textBlue,
  },
  subtitle: {
    fontSize: '14px',
    fontWeight: 400,
    color: theme.palette.text.secondary,
  },
  inviteCode: {
    fontSize: '12px',
    fontWeight: 400,
    color: theme.palette.text.secondary,
    display: 'flex',
    gap: 1,
    alignItems: 'center',
  },
  code: {
    fontSize: '12px',
    fontWeight: 500,
    color: theme.palette.custom.textBlue,
  },
  table: {
    minWidth: 650,
  },
  tableHeaderCell: {
    fontSize: '14px',
    fontWeight: 500,
    color: theme.palette.custom.textBlue,
  },
  tableBodyCell: {
    fontSize: '14px',
    fontWeight: 400,
    color: theme.palette.text.secondary,
  },
  sectionChip: {
    fontSize: '11px',
    height: '22px',
    '& .MuiChip-label': { px: 1 },
  },
};

interface SectionsDropdownProps {
  memberEmail: string;
  currentSections: string[];
  onUpdate: (email: string, sections: string[]) => void;
  isUpdating: boolean;
  readOnly?: boolean;
}

const SectionsDropdown = ({
  memberEmail,
  currentSections,
  onUpdate,
  isUpdating,
  readOnly = false,
}: SectionsDropdownProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selected, setSelected] = useState<string[]>(currentSections);

  useEffect(() => {
    setSelected(currentSections);
  }, [currentSections]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggle = (key: string) => {
    if (readOnly) return;
    setSelected(prev =>
      prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]
    );
  };

  const handleSave = () => {
    if (readOnly) {
      handleClose();
      return;
    }
    onUpdate(memberEmail, selected);
    handleClose();
  };

  const hasChanges =
    !readOnly &&
    (selected.length !== currentSections.length ||
      selected.some(s => !currentSections.includes(s)));

  return (
    <>
      <Button
        size="small"
        onClick={handleOpen}
        endIcon={<CaretDown size={14} />}
        disabled={isUpdating}
        sx={{
          textTransform: 'none',
          fontSize: '12px',
          color: '#64748B',
          border: '1px solid #DCE0E7',
          borderRadius: '6px',
          px: 1.5,
          py: 0.5,
          minWidth: 0,
        }}
      >
        {selected.length > 0
          ? `${selected.length} permission${selected.length > 1 ? 's' : ''}`
          : 'No permissions'}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            maxHeight: 350,
            width: 260,
            borderRadius: '8px',
            boxShadow: '0px 4px 16px rgba(0,0,0,0.12)',
          },
        }}
      >
        {readOnly
          ? selected.map(key => (
              <MenuItem key={key} dense sx={{ py: 0.5 }}>
                <ListItemText
                  primary={SECTION_LABEL_MAP[key] || key}
                  primaryTypographyProps={{ fontSize: '13px' }}
                />
              </MenuItem>
            ))
          : SECTION_OPTIONS.map(section => (
              <MenuItem
                key={section.key}
                onClick={() => handleToggle(section.key)}
                dense
                sx={{ py: 0.5 }}
              >
                <Checkbox
                  checked={selected.includes(section.key)}
                  size="small"
                  sx={{ p: 0.5, mr: 1 }}
                />
                <ListItemText
                  primary={section.label}
                  primaryTypographyProps={{ fontSize: '13px' }}
                />
              </MenuItem>
            ))}
        {!readOnly && (
          <>
            <Divider />
            <Box sx={{ px: 1.5, py: 1, display: 'flex', gap: 1 }}>
              <Button
                size="small"
                onClick={handleClose}
                sx={{ textTransform: 'none', fontSize: '12px', flex: 1 }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleSave}
                disabled={!hasChanges || isUpdating}
                sx={{
                  textTransform: 'none',
                  fontSize: '12px',
                  flex: 1,
                  boxShadow: 'none',
                }}
              >
                {isUpdating ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </>
        )}
      </Menu>
    </>
  );
};

interface UsersListProps {
  isInviteModalOpen: boolean;
  onCloseInviteModal: () => void;
}

const UsersList: FC<UsersListProps> = ({
  isInviteModalOpen,
  onCloseInviteModal,
}) => {
  const { copied, copy } = useCopyToClipboard();
  const { user: currentUser } = useUser();
  const queryClient = useQueryClient();
  const { alert, setAlert, showAlert } = useAlert();
  const [organization, setOrganization] = useState<IGetOrganization | null>(
    null
  );
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [userToRemove, setUserToRemove] = useState<{
    email: string;
    name: string;
  } | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['get-organization'],
    queryFn: getOrganization,
    refetchOnMount: false,
    retry: 0,
  });

  const { mutateAsync: removeUser, isPending: isRemovePending } = useMutation({
    mutationFn: removeUserApi,
    onSuccess: () => {
      showAlert('Member removed successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['get-organization'] });
      setRemoveDialogOpen(false);
      setUserToRemove(null);
    },
    onError: (error: any) => {
      showAlert(
        error?.response?.data?.message || 'Failed to remove member',
        'error'
      );
    },
  });

  const { mutateAsync: updateSections, isPending: isUpdateSectionsPending } =
    useMutation({
      mutationFn: updateMemberSectionsApi,
      onSuccess: () => {
        showAlert('Permissions updated successfully', 'success');
        queryClient.invalidateQueries({ queryKey: ['get-organization'] });
      },
      onError: (error: any) => {
        showAlert(
          error?.response?.data?.message || 'Failed to update permissions',
          'error'
        );
      },
    });

  const isOwner = currentUser?.organization?.role === 'owner';

  const pendingInvites = useMemo(() => {
    return (organization?.pendingInvites || []).filter(
      invite => !invite.used && invite.email
    );
  }, [organization?.pendingInvites]);

  const handleCopyClick = () => {
    if (organization) {
      copy(organization.organizationCode);
    }
  };

  const handleRemoveClick = (email: string, name: string) => {
    setUserToRemove({ email, name });
    setRemoveDialogOpen(true);
  };

  const handleConfirmRemove = () => {
    if (userToRemove) {
      void removeUser({ email: userToRemove.email });
    }
  };

  const handleUpdateSections = (email: string, sections: string[]) => {
    void updateSections({ email, sections });
  };

  const rows = useMemo(() => {
    if (!organization) return [];

    const membersRows =
      organization.members?.map(member => ({
        id: member.user?._id || member.user?.email || Math.random().toString(),
        type: 'member' as const,
        name: member.user?.name,
        email: member.user?.email,
        role: member.role,
        sections: member.sections || [],
        isSelf: member.user?._id === currentUser?._id,
        isOwnerMember: member.role === 'owner',
      })) || [];

    const pendingRows =
      pendingInvites.map(invite => ({
        id: invite._id || invite.email || Math.random().toString(),
        type: 'invite' as const,
        name: '',
        email: invite.email,
        role: 'member',
        sections: invite.sections || [],
      })) || [];

    return [...membersRows, ...pendingRows];
  }, [organization, pendingInvites, currentUser?._id]);

  const allColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      minWidth: 200,
      flex: 1,
      headerClassName: 'custom-header',
      renderCell: (params: GridRenderCellParams) => {
        const isSelf = params.row.isSelf;
        const isInvite = params.row.type === 'invite';
        const displayName =
          params.value || (isInvite ? 'Pending Invite' : 'Unnamed');

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Typography
              sx={{
                overflow: 'hidden',
                color: '#302B3F',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontFamily: '"DM Sans"',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: 'normal',
                letterSpacing: '-0.14px',
              }}
            >
              {displayName}
            </Typography>
            {isSelf && (
              <Typography
                component="span"
                sx={{
                  fontSize: '11px',
                  ml: 0.5,
                  color: '#5A4CE6',
                }}
              >
                (You)
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'email',
      headerName: 'Email',
      minWidth: 250,
      flex: 1.2,
      headerClassName: 'custom-header',
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {params.row.type === 'invite' && (
            <EnvelopeSimple size={16} color="#64748B" />
          )}
          <Typography
            sx={{
              overflow: 'hidden',
              color: '#6B6F80',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontFamily: '"DM Sans"',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: 'normal',
              letterSpacing: '-0.14px',
            }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'role',
      headerName: 'Role',
      minWidth: 140,
      flex: 0.6,
      headerClassName: 'custom-header',
      renderCell: (params: GridRenderCellParams) => {
        const role = params.row.type === 'invite' ? 'Member' : params.value;

        return (
          <Chip
            label={
              role ? role.charAt(0).toUpperCase() + role.slice(1) : 'Member'
            }
            size="small"
            sx={{
              ...styles.sectionChip,
              bgcolor: role === 'owner' ? '#EDE9FE' : '#F1F5F9',
              color: role === 'owner' ? '#5A4CE6' : '#64748B',
            }}
          />
        );
      },
    },
    {
      field: 'sections',
      headerName: 'Permissions',
      minWidth: 220,
      flex: 1.3,
      headerClassName: 'custom-header',
      renderCell: (params: GridRenderCellParams) => {
        const sections: string[] = params.value || [];
        const isMemberOwner = params.row.isOwnerMember;
        const isSelf = params.row.isSelf;

        if (params.row.type === 'member') {
          if (isMemberOwner) {
            return (
              <Typography sx={{ fontSize: '12px', color: '#64748B' }}>
                All permissions
              </Typography>
            );
          }

          if (isOwner && !isSelf) {
            return (
              <SectionsDropdown
                memberEmail={params.row.email}
                currentSections={sections}
                onUpdate={handleUpdateSections}
                isUpdating={isUpdateSectionsPending}
              />
            );
          }

          if (sections.length > 0) {
            return (
              <SectionsDropdown
                memberEmail={params.row.email}
                currentSections={sections}
                onUpdate={handleUpdateSections}
                isUpdating={false}
                readOnly
              />
            );
          }

          return (
            <Typography sx={{ fontSize: '12px', color: '#94A3B8' }}>
              No permissions assigned
            </Typography>
          );
        }

        if (isOwner) {
          return (
            <SectionsDropdown
              memberEmail={params.row.email}
              currentSections={sections}
              onUpdate={handleUpdateSections}
              isUpdating={isUpdateSectionsPending}
            />
          );
        }

        if (sections.length > 0) {
          return (
            <SectionsDropdown
              memberEmail={params.row.email}
              currentSections={sections}
              onUpdate={handleUpdateSections}
              isUpdating={false}
              readOnly
            />
          );
        }

        return (
          <Typography sx={{ fontSize: '12px', color: '#94A3B8' }}>
            No permissions
          </Typography>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 140,
      flex: 0.7,
      headerAlign: 'center',
      align: 'center',
      headerClassName: 'custom-header',
      renderCell: (params: GridRenderCellParams) =>
        params.row.type === 'member' ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              gap: 0.5,
            }}
          >
            <Dot size={28} color="#37B77D" />
            <Typography
              sx={{
                color: '#37B77D',
                fontFamily: '"DM Sans"',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
                letterSpacing: '-0.14px',
              }}
            >
              Active
            </Typography>
          </Box>
        ) : (
          <Chip
            label="Pending"
            size="small"
            sx={{
              ...styles.sectionChip,
              bgcolor: '#FEF3C7',
              color: '#92400E',
            }}
          />
        ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      minWidth: 120,
      flex: 0.6,
      sortable: false,
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      headerClassName: 'custom-header',
      renderCell: (params: GridRenderCellParams) => {
        const isSelf = params.row.isSelf;
        const isMemberOwner = params.row.isOwnerMember;

        if (params.row.type === 'member') {
          if (!isOwner || isSelf || isMemberOwner) {
            return null;
          }

          return (
            <Tooltip title="Remove member">
              <IconButton
                size="small"
                color="error"
                onClick={() =>
                  handleRemoveClick(params.row.email, params.row.name)
                }
              >
                <Trash size={18} />
              </IconButton>
            </Tooltip>
          );
        }

        return (
          <Tooltip title="Remove pending invite">
            <IconButton
              size="small"
              color="error"
              onClick={() =>
                handleRemoveClick(params.row.email, 'Pending invite')
              }
            >
              <Trash size={18} />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  const columns = isOwner
    ? allColumns
    : allColumns.filter(column => column.field !== 'actions');

  useEffect(() => {
    if (data) {
      setOrganization(data?.data?.data);
    }
  }, [data]);

  if (isError) {
    return (
      <Typography color="error">Error loading organization data</Typography>
    );
  }

  return (
    <Box sx={styles.root}>
      {isLoading ? (
        <Stack spacing={2}>
          <Skeleton variant="text" width="60%" height={40} />
          <Skeleton variant="text" width="80%" height={20} />
          <Skeleton variant="rectangular" width="100%" height={100} />
        </Stack>
      ) : organization ? (
        <Stack spacing={2} sx={{ height: '100%' }}>
          <Box>
            <Box sx={styles.header}>
              <Typography variant="h3" sx={styles.title}>
                {organization.name}
              </Typography>
              <Typography sx={styles.inviteCode}>
                Invite Code :
                <Typography sx={styles.code}>
                  {organization.organizationCode}
                  <IconButton
                    size="small"
                    sx={{ p: 0.5 }}
                    onClick={handleCopyClick}
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                  </IconButton>
                </Typography>
              </Typography>
            </Box>
            <Typography variant="h6" sx={styles.subtitle}>
              Manage your team members and collaborate efficiently within your
              organization
            </Typography>
          </Box>
          <Divider />

          <Box
            sx={{
              width: '100%',
              height: '100%',
              border: '1px solid #DCE0E7',
              borderRadius: '8px',
              overflow: 'hidden',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              bgcolor: '#FFFFFF',
            }}
          >
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={row => row.id}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 100 },
                },
              }}
              pageSizeOptions={[10, 20, 50, 75, 100]}
              disableRowSelectionOnClick
              sx={{
                '& .MuiDataGrid-row': {
                  alignItems: 'center',
                },
                '& .custom-header': {
                  bgcolor: '#F8F9FC',
                  color: 'custom.textBlue',
                  fontSize: '14px',
                  fontWeight: 600,
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  '&:focus, &:focus-within': {
                    outline: 'none !important',
                  },
                },
                border: 'none',
                '& .MuiDataGrid-cell:focus-within': {
                  outline: 'none !important',
                },
              }}
            />
          </Box>
        </Stack>
      ) : (
        <CreateOrJoinOrg />
      )}

      <AddMemberToOrganisationModal
        open={isInviteModalOpen}
        onClose={onCloseInviteModal}
      />

      <DeleteModal
        open={removeDialogOpen}
        onClose={() => {
          setRemoveDialogOpen(false);
          setUserToRemove(null);
        }}
        onDelete={handleConfirmRemove}
        isPending={isRemovePending}
        title="Remove Member"
        description={`Are you sure you want to remove ${
          userToRemove?.name
            ? `${userToRemove.name} (${userToRemove.email})`
            : userToRemove?.email
        } from the organization? This action cannot be undone.`}
        confirmButtonText="Remove"
      />

      <MessagePopup alert={alert} setAlert={setAlert} />
    </Box>
  );
};

export default UsersList;
